'use client';
import ScrollLink from "../Helpers/ScrollLink";
import CookieConsentBanner from "../CookieConsentBanner";
import { LangLink } from "../../i18n/links";
import { useTranslation } from "react-i18next";
import { useParams } from 'next/navigation';
import { defaultLang, SUPPORTED } from '../../config/languages';
import { getAffiliateCodeFromCurrentUrl, getAppDownloadUrl } from '../../lib/appDownloadLink';

const CallToAction = ({ variant = null, showNote = true, showHeading = true } = {}) => {
    const { t } = useTranslation(['call_to_action', 'home']);
    const { lang = defaultLang } = useParams() || {};
    const activeLang = SUPPORTED.includes(lang) ? lang : defaultLang;
    // const { t: tc } = useTranslation('call_to_action');
    const label = (key, fallback) => t(`call_to_action:labels.${key}`, fallback);

    const getButtons = (variant = "") => {
      return <div
                className={`cta-buttons ${variant}`}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  alignItems: 'stretch',
                  gap: '12px',
                  marginTop: '20px',
                  padding: 0,
                }}
              >
                <a
                  id="cta_try_adcast_ios"
                  target="_blank"
                  href={getAppDownloadUrl('ios', activeLang)}
                  onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('ios', activeLang, getAffiliateCodeFromCurrentUrl()); }}
                  aria-label={label('app_store_aria', 'Download AdCast on App Store')}
                  description={'App store clicked'}
                  style={{ display: 'block', flex: '1 1 150px', maxWidth: '200px', minWidth: 0 }}
                >
                  <img
                    className="cta-store-badge"
                    style={{ display: 'block', width: '100%', height: 'auto' }}
                    src="/assets/icons/app_store.svg"
                    alt={label('app_store_alt', 'Download AdCast from App Store')}
                    width="200"
                    height="72"
                    loading="lazy"
                  />
                </a>

                <a
                  id="cta_try_adcast_android"
                  target="_blank"
                  href={getAppDownloadUrl('android', activeLang)}
                  onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('android', activeLang, getAffiliateCodeFromCurrentUrl()); }}
                  aria-label={label('google_play_aria', 'Get AdCast on Google Play')}
                  description={'Play store clicked'}
                  style={{ display: 'block', flex: '1 1 150px', maxWidth: '200px', minWidth: 0 }}
                >
                  <img
                    className="cta-store-badge"
                    style={{ display: 'block', width: '100%', height: 'auto' }}
                    src="/assets/icons/play_store.svg"
                    alt={label('google_play_alt', 'Download AdCast from Google Play Store')}
                    width="200"
                    height="72"
                    loading="lazy"
                  />
                </a>

                <a
                  id="cta_try_adcast_web"
                  target="_blank"
                  href="https://player.adcast.app"
                  aria-label={label('web_player_aria', 'Try AdCast Web Player')}
                  description={'WebPlayer clicked'}
                  className="cta-web-player-link"
                  style={{ display: 'block', flex: '1 1 150px', maxWidth: '200px', minWidth: 0, textDecoration: 'none' }}
                >
                  <div
                    className="cta-web-player"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      aspectRatio: '200 / 72',
                      minHeight: '56px',
                      backgroundColor: '#262626',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  >
                    <p style={{ margin: 0 }}>{label('web_player_text', 'Web Player')}</p>
                  </div>
                </a>
              </div>;
    }

    if (variant === 'mobile-card') {
      return (
        <div className="cta-mobile-card">
          <div className="cta-mobile-card-buttons">
            <a
              id="cta_try_adcast_ios"
              target="_blank"
              rel="noopener noreferrer"
              href={getAppDownloadUrl('ios', activeLang)}
              onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('ios', activeLang, getAffiliateCodeFromCurrentUrl()); }}
              aria-label={label('app_store_aria', 'Download AdCast on App Store')}
              description={'App store clicked'}
            >
              <img
                className="cta-store-badge"
                src="/assets/icons/app_store.svg"
                alt={label('app_store_alt', 'Download AdCast from App Store')}
                width="140"
                height="42"
                loading="lazy"
              />
            </a>
            <a
              id="cta_try_adcast_android"
              target="_blank"
              rel="noopener noreferrer"
              href={getAppDownloadUrl('android', activeLang)}
              onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('android', activeLang, getAffiliateCodeFromCurrentUrl()); }}
              aria-label={label('google_play_aria', 'Get AdCast on Google Play')}
              description={'Play store clicked'}
            >
              <img
                className="cta-store-badge"
                src="/assets/icons/play_store.svg"
                alt={label('google_play_alt', 'Download AdCast from Google Play Store')}
                width="140"
                height="42"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      );
    }

    if (variant === 'buttons') {
      return <>
        {getButtons()}
        {showNote && (
          <p className="hero-cta-note">
            {t('bottom.point5')}
          </p>
        )}
      </>;
    }

    if (variant === 'header') {
        return (
            <>
              <p>
                {t('bottom.descriptionStart')} <strong>AdCast Player</strong>{t('bottom.descriptionMiddle')}
                <strong>{t('bottom.descriptionStrong')}</strong>{t('bottom.descriptionEnd')}
              </p>

              {getButtons()}
              <p>
                {t('bottom.point5')}
              </p>
            </>
            );
    }

    if (variant === 'bottom') {
      return (
        <>
          <h3>{t('bottom.title')}</h3>

          <p>
            {t('bottom.descriptionStart')} <strong>AdCast Player</strong>{t('bottom.descriptionMiddle')}
            <strong>{t('bottom.descriptionStrong')}</strong>{t('bottom.descriptionEnd')}
          </p>

          <p style={{ marginTop: '20px' }}>
            {t('bottom.noComplexity')}
          </p>

          <p style={{ marginTop: '20px' }}>
            {t('bottom.workflow')}
          </p>

          <ul style={{ marginTop: '20px' }}>
            <li>{t('bottom.point1')}</li>
            <li>{t('bottom.point2')}</li>
            <li>{t('bottom.point3')}</li>
            <li>{t('bottom.point4')}</li>
          </ul>

          {getButtons()}
        </>
      );
    }

    if (variant === 'home') {
      return (
        <div className="dl3-grid">
          <div className="dl3-copy">
            {showHeading && (
              <>
                <h2 className="dl3-headline">{t('home:download_area.title')}</h2>
                <p className="dl3-description">{t('home:download_area.description')}</p>
              </>
            )}

            <div className="dl3-group">
              <p className="dl3-group-label">{t('home:download_area.phone_label')}</p>
              <div className="dl3-group-buttons">
                <a
                  id="cta_try_adcast_ios"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={getAppDownloadUrl('ios', activeLang)}
                  onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('ios', activeLang, getAffiliateCodeFromCurrentUrl()); }}
                  aria-label={label('app_store_aria', 'Download AdCast on App Store')}
                  description={'App store clicked'}
                >
                  <img className="dl3-store-badge" src="/assets/icons/app_store.svg" alt={label('app_store_alt', 'Download AdCast from App Store')} loading="lazy" />
                </a>
                <a
                  id="cta_try_adcast_android"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={getAppDownloadUrl('android', activeLang)}
                  onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('android', activeLang, getAffiliateCodeFromCurrentUrl()); }}
                  aria-label={label('google_play_aria', 'Get AdCast on Google Play')}
                  description={'Play store clicked'}
                >
                  <img className="dl3-store-badge" src="/assets/icons/play_store.svg" alt={label('google_play_alt', 'Download AdCast from Google Play Store')} loading="lazy" />
                </a>
              </div>
              <div className="dl3-app-qr-codes" aria-label={label('mobile_qr_codes_label', 'QR codes for mobile app downloads')}>
                <div className="dl3-app-qr-code">
                  <img src="/assets/download/adcast-ios-qr.svg" alt="" width="88" height="88" loading="lazy" />
                  <span>{label('ios_qr_label', 'Scan for iPhone')}</span>
                </div>
                <div className="dl3-app-qr-code">
                  <img src="/assets/download/adcast-android-qr.svg" alt="" width="88" height="88" loading="lazy" />
                  <span>{label('android_qr_label', 'Scan for Android')}</span>
                </div>
              </div>
            </div>

            <div className="dl3-group">
              <p className="dl3-group-label">{t('home:download_area.tv_label')}</p>
              <div className="dl3-group-buttons">
                <a
                  id="cta_try_adcast_player"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://play.google.com/store/apps/details?id=com.adcast.player"
                  aria-label={label('player_google_play_aria', 'Download AdCast Player on Google Play')}
                  description={'AdCast Player Play Store clicked'}
                >
                  <img className="dl3-store-badge" src="/assets/icons/play_store.svg" alt={label('player_google_play_alt', 'Download AdCast Player from Google Play')} loading="lazy" />
                </a>
                <a
                  id="cta_try_adcast_web"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://player.adcast.app"
                  aria-label={label('web_player_aria', 'Try AdCast Web Player')}
                  description={'WebPlayer clicked'}
                  className="dl3-web-player"
                >
                  <span className="dl3-web-player-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M10 8.5L16 12L10 15.5V8.5Z" fill="currentColor" />
                    </svg>
                  </span>
                  <span className="dl3-web-player-text">
                    <span className="dl3-web-player-eyebrow">{label('web_player_eyebrow', 'No install needed')}</span>
                    {label('web_player_text', 'Web Player')}
                  </span>
                </a>
                <a
                  id="cta_try_adcast_appletv"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://apps.apple.com/us/app/adcast-digital-signage-player/id6759452988"
                  aria-label={label('apple_tv_aria', 'Download AdCast Player on Apple TV')}
                  description={'Apple TV Player clicked'}
                  className="dl3-web-player dl3-apple-tv-player"
                >
                  <span className="dl3-web-player-icon" aria-hidden="true">
                    <svg viewBox="0 0 44 24" fill="currentColor">
                      <path d="M16.7 12.7c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.6-1.3-.1-2.5.8-3.1.8-.6 0-1.6-.8-2.6-.8-1.3 0-2.6.8-3.3 2-1.4 2.5-.4 6.2 1 8.2.7 1 1.5 2.1 2.6 2 1.1 0 1.5-.7 2.9-.7 1.3 0 1.7.7 2.9.7 1.2 0 2-1.1 2.6-2.1.8-1.2 1.1-2.4 1.1-2.5-.1 0-2.9-1.1-2.9-4.8ZM14.7 6.6c.5-.6.9-1.5.8-2.4-.8 0-1.8.5-2.4 1.1-.5.6-.9 1.5-.8 2.3.9.1 1.8-.4 2.4-1Z" />
                      <text x="24" y="18" fill="currentColor" fontFamily="Arial, sans-serif" fontSize="16" fontWeight="700">tv</text>
                    </svg>
                  </span>
                  <span className="dl3-web-player-text">
                    <span className="dl3-web-player-eyebrow">{label('apple_tv_note', 'WebView not supported')}</span>
                    {label('apple_tv_eyebrow', 'Apple TV Player')}
                  </span>
                </a>
              </div>
            </div>

            <div className="dl3-group">
              <p className="dl3-group-label">{t('home:download_area.desktop_label')}</p>
              <div className="dl3-group-buttons">
                <a
                  id="cta_download_adcast_player_windows"
                  href="https://player.adcast.app/downloads/2.2.4/AdCast-Player-Setup-2.2.4.exe"
                  aria-label="Download AdCast Player for Windows"
                  description="AdCast Player for Windows downloaded"
                  className="dl3-web-player"
                >
                  <span className="dl3-web-player-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 5.5L10.5 4.4V11.4H3V5.5ZM11.5 4.25L21 3V11.4H11.5V4.25ZM3 12.4H10.5V19.4L3 18.3V12.4ZM11.5 12.4H21V20.8L11.5 19.55V12.4Z" />
                    </svg>
                  </span>
                  <span className="dl3-web-player-text">
                    <span className="dl3-web-player-eyebrow">{label('desktop_player_for', 'Player for')}</span>
                    Windows
                  </span>
                </a>
                <a
                  id="cta_download_adcast_player_macos"
                  href="https://player.adcast.app/downloads/2.2.4/AdCast-Player-2.2.4-universal.dmg"
                  aria-label="Download AdCast Player for macOS"
                  description="AdCast Player for macOS downloaded"
                  className="dl3-web-player"
                >
                  <span className="dl3-web-player-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.53 3.5c.1 1-.28 2-.83 2.7-.56.7-1.5 1.24-2.4 1.17-.12-.97.32-1.98.86-2.62.6-.72 1.63-1.24 2.37-1.25ZM19.4 17.1c-.34.79-.5 1.14-.93 1.85-.6 1-1.45 2.24-2.5 2.25-.94.01-1.18-.6-2.45-.6-1.27.01-1.55.61-2.49.6-1.05-.01-1.86-1.13-2.46-2.13-1.68-2.79-1.86-6.06-.82-7.8.74-1.25 1.9-1.98 2.99-1.98 1.1 0 1.8.62 2.7.62.88 0 1.42-.62 2.7-.62.97 0 2 .53 2.73 1.44-2.4 1.32-2.01 4.76.53 5.38Z" />
                    </svg>
                  </span>
                  <span className="dl3-web-player-text">
                    <span className="dl3-web-player-eyebrow">{label('desktop_player_for', 'Player for')}</span>
                    macOS
                  </span>
                </a>
                <a
                  id="cta_download_adcast_player_linux"
                  href="https://player.adcast.app/downloads/2.2.4/AdCast-Player-2.2.4.AppImage"
                  aria-label="Download AdCast Player for Linux"
                  description="AdCast Player for Linux downloaded"
                  className="dl3-web-player"
                >
                  <span className="dl3-web-player-icon" aria-hidden="true">
                    <svg viewBox="0 0 485 485" fill="currentColor">
                      <path d="M139.157,383.053c16.613,1.984,35.279,12.746,50.9,14.646c15.705,1.983,20.565-10.695,20.565-10.695 s17.673-3.951,36.253-4.406c18.598-0.521,36.203,3.868,36.203,3.868s3.414,7.819,9.787,11.233 c6.373,3.48,20.094,3.951,28.888-5.314c8.811-9.332,32.319-21.087,45.519-28.435c13.284-7.365,10.846-18.597,2.506-22.011 c-8.34-3.413-15.167-8.794-14.646-19.119c0.454-10.24-7.365-17.067-7.365-17.067s6.844-22.532,0.471-41.197 c-6.373-18.58-27.392-48.461-43.551-70.926c-16.159-22.532-2.439-48.545-17.151-81.789c-14.714-33.294-52.867-31.326-73.432-17.135 c-20.565,14.192-14.26,49.386-13.268,66.084c0.992,16.613,0.454,28.501-1.446,32.773c-1.9,4.338-15.167,20.094-23.978,33.294 c-8.794,13.267-15.168,40.659-21.608,51.959c-6.306,11.233-1.9,21.473-1.9,21.473s-4.405,1.513-7.886,8.879 c-3.414,7.281-10.241,10.761-22.533,13.133c-12.208,2.505-12.208,10.375-9.265,19.186c2.959,8.794,0,13.721-3.414,24.954 C105.393,377.672,122.477,381.085,139.157,383.053z M311.849,331.816c8.727,3.817,21.271-1.497,25.088-5.314 c3.801-3.8,6.491-9.45,6.491-9.45s3.817,1.9,3.43,7.936c-0.404,6.121,2.623,14.848,8.34,17.875 c5.717,3.01,14.444,7.213,9.921,11.417c-4.608,4.204-30.099,14.461-37.716,22.465c-7.55,7.953-17.471,14.461-23.508,12.544 c-6.103-1.9-11.434-10.24-8.81-22.448c2.707-12.157,4.994-25.492,4.607-33.109c-0.403-7.617-1.9-17.874,0-19.388 c1.9-1.497,4.927-0.774,4.927-0.774S303.104,328.017,311.849,331.816z M255.216,126.033c8.407,0,15.184,8.34,15.184,18.597 c0,7.281-3.414,13.587-8.408,16.614c-1.261-0.521-2.573-1.11-4.019-1.716c3.027-1.496,5.128-5.313,5.128-9.719 c0-5.767-3.547-10.509-8.004-10.509c-4.338,0-7.954,4.741-7.954,10.509c0,2.103,0.522,4.204,1.379,5.852 c-2.623-1.06-4.994-1.967-6.894-2.69c-0.992-2.506-1.581-5.331-1.581-8.34C240.048,134.374,246.807,126.033,255.216,126.033z M234.398,158.083c4.137,0.723,15.503,5.65,19.708,7.163c4.204,1.446,8.861,4.136,8.407,6.827c-0.521,2.774-2.69,2.774-8.407,6.255 c-5.65,3.414-17.992,11.031-21.944,11.552c-3.935,0.521-6.171-1.699-10.376-4.406c-4.204-2.758-12.089-9.198-10.106-12.611 c0,0,6.171-4.725,8.862-7.146C223.233,163.211,230.194,157.309,234.398,158.083z M216.271,128.993 c6.625,0,12.023,7.886,12.023,17.605c0,1.766-0.202,3.413-0.521,5.061c-1.648,0.521-3.295,1.379-4.877,2.758 c-0.773,0.656-1.497,1.244-2.152,1.9c1.042-1.967,1.446-4.792,0.975-7.752c-0.908-5.246-4.456-9.13-7.937-8.609 c-3.498,0.589-5.583,5.398-4.742,10.712c0.925,5.381,4.405,9.264,7.953,8.676c0.202-0.067,0.387-0.134,0.589-0.202 c-1.698,1.648-3.279,3.094-4.927,4.271c-4.792-2.237-8.34-8.929-8.34-16.815C204.316,136.812,209.629,128.993,216.271,128.993z M179.362,256.216c6.827-10.761,11.232-34.286,18.059-42.105c6.895-7.802,12.208-24.432,9.787-31.78 c0,0,14.713,17.605,24.954,14.713c10.257-2.96,33.311-20.094,36.724-17.151c3.414,2.959,32.773,67.529,35.732,88.094 c2.96,20.548-1.967,36.254-1.967,36.254s-11.232-2.96-12.679,3.867c-1.446,6.894-1.446,31.865-1.446,31.865 s-15.184,21.019-38.692,24.499c-23.508,3.414-35.279,0.925-35.279,0.925l-13.2-15.117c0,0,10.258-1.513,8.811-11.821 c-1.446-10.24-31.344-24.433-36.725-37.179C168.062,288.534,172.451,266.994,179.362,256.216z M121.232,333.33 c1.177-5.044,16.411-5.044,22.263-8.592c5.852-3.548,7.028-13.738,11.754-16.428c4.657-2.758,13.267,7.029,16.815,12.543 c3.481,5.381,16.816,28.906,22.264,34.757c5.515,5.902,10.577,13.721,8.996,20.75c-1.496,7.028-9.786,12.157-9.786,12.157 c-7.415,2.287-28.098-6.642-37.497-10.577c-9.399-3.951-33.311-5.129-36.388-8.609c-3.161-3.548,1.513-11.367,2.757-18.783 C123.519,343.049,120.039,338.391,121.232,333.33z" />
                    </svg>
                  </span>
                  <span className="dl3-web-player-text">
                    <span className="dl3-web-player-eyebrow">{label('desktop_player_for', 'Player for')}</span>
                    Linux
                  </span>
                </a>
              </div>
            </div>
          </div>
          <div className="dl3-art">
            <div className="dl3-phone dl3-phone-displays">
              <img src="/assets/download/mobile-displays.webp" alt="AdCast displays list in the mobile app" loading="lazy" />
            </div>
            <div className="dl3-phone dl3-phone-home">
              <img src="/assets/download/mobile-home.webp" alt="AdCast mobile dashboard" loading="lazy" />
            </div>
            <div className="dl3-phone dl3-phone-templates">
              <img src="/assets/download/mobile-templates.webp" alt="AdCast templates in the mobile app" loading="lazy" />
            </div>
          </div>
        </div>
      );
    }

    return (
          <div className="row">
            <h2>{t('download.title')}</h2>
            <div className="col-lg-9 col-xl-9 align-self-center">
              <div className=" hbm">
                
                <p className="lead">
                  {t('download.description')}
                </p>
                <p className="lead">
                  {t('download.description2')}
                </p>

              </div>
              {/* <div className="btn2_group">
                <a id="cta_try_adcast_ios" target="_blank" href="https://apps.apple.com/us/app/adcast-app/id6744039593" aria-label="Download AdCast on App Store" description={'App store clicked'}><img style={{ width: '200px', marginBottom: '10px' }} src="/assets/icons/app_store.svg" alt="Download AdCast from App Store" loading="lazy" /></a> &nbsp;
                <a id="cta_try_adcast_android" target="_blank" href="https://play.google.com/store/apps/details?id=com.adcast.app" aria-label="Get AdCast on Google Play" description={'Play store clicked'}><img style={{ width: '200px', marginBottom: '10px' }} src="/assets/icons/play_store.svg" alt="Download AdCast from Google Play Store" loading="lazy" /></a> &nbsp;
              </div> */}
              {getButtons()}
            </div>
            <div className="col-lg-3 col-xl-3 align-self-center">
              <div className="apps_app3 text-right">
                <img width="200" src="/assets/img/feature/download_adcast.png" alt="Download AdCast Digital Signage" loading="lazy" />
              </div>
            </div>
          </div>

        // <footer className="footer_area">
        //     <div className="footer footer1 section-padding2">
        //         <div className="container">
        //             <div className="row g-4">

        //                 {/* Brand column */}
        //                 <div className="col-12 col-sm-6 col-lg-4">
        //                     <div className="single_footer">
        //                         <div className="footer_logo" style={{ marginBottom: '20px' }}>
        //                             <ScrollLink to="/" aria-label="AdCast Digital Signage - Home">
        //                                 <img src="/assets/icons/logo.svg" alt="AdCast" style={{ height: '32px' }} />
        //                             </ScrollLink>
        //                         </div>
        //                         <p style={{ fontSize: '14px', lineHeight: '1.7', marginBottom: '16px' }}>
        //                             {t("text")}
        //                         </p>
        //                         <p style={{ fontSize: '13px', lineHeight: '1.7' }}>
        //                         </p>
        //                         <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
        //                             <a href="https://apps.apple.com/us/app/adcast-app/id6744039593" target="_blank" rel="noopener noreferrer">
        //                                 <img src="/assets/icons/app_store.svg" alt="Download on App Store" style={{ height: '36px' }} />
        //                             </a>
        //                             <a href="https://play.google.com/store/apps/details?id=com.adcast.app" target="_blank" rel="noopener noreferrer">
        //                                 <img src="/assets/icons/play_store.svg" alt="Get it on Google Play" style={{ height: '36px' }} />
        //                             </a>
        //                         </div>
        //                     </div>
        //                 </div>

        //                 {/* Product links */}
        //                 <div className="col-6 col-sm-6 col-lg-2 ms-auto">
        //                     <div className="single_footer">
        //                         <h3 className="footer-col-title">{t("product")}</h3>
        //                         <ul className="footer-links">
        //                             <li><LangLink to="/blog">{t("header:blog")}</LangLink></li>
        //                             <li><LangLink to="/solutions">{t("header:solutions.title")}</LangLink></li>
        //                             <li><LangLink to="/pricing">{t("header:pricing")}</LangLink></li>
        //                             <li><LangLink to="/support">{t("faq_and_support")}</LangLink></li>
        //                         </ul>
        //                     </div>
        //                 </div>

        //                 {/* Solutions links */}
        //                 <div className="col-6 col-sm-6 col-lg-2">
        //                     <div className="single_footer">
        //                         <h3 className="footer-col-title">{t("header:solutions.title")}</h3>
        //                         <ul className="footer-links">
        //                             <li><LangLink to="/solutions/digital-signage-for-restaurants">{t("header:solutions.restaurants")}</LangLink></li>
        //                             <li><LangLink to="/solutions/digital-signage-for-gyms">{t("header:solutions.gyms")}</LangLink></li>
        //                             <li><LangLink to="/solutions/digital-signage-for-healthcare">{t("header:solutions.healthcare")}</LangLink></li>
        //                             <li><LangLink to="/solutions/digital-signage-for-lobbies">{t("header:solutions.lobbies")}</LangLink></li>
        //                             <li><LangLink to="/solutions/digital-signage-for-salons">{t("header:solutions.salons")}</LangLink></li>
        //                         </ul>
        //                     </div>
        //                 </div>

        //                 {/* Comparisons links */}
        //                 <div className="col-6 col-sm-6 col-lg-2">
        //                     <div className="single_footer">
        //                         <h3 className="footer-col-title">{t("comparisons")}</h3>
        //                         <ul className="footer-links">
        //                             <li><LangLink to={`/${compareSection}/adcast-vs-yodeck`}>AdCast vs Yodeck</LangLink></li>
        //                             <li><LangLink to={`/${compareSection}/adcast-vs-screencloud`}>AdCast vs ScreenCloud</LangLink></li>
        //                             <li><LangLink to={`/${compareSection}/adcast-vs-novisign`}>AdCast vs NoviSign</LangLink></li>
        //                         </ul>
        //                     </div>
        //                 </div>

        //                 {/* Legal links */}
        //                 <div className="col-6 col-sm-6 col-lg-2">
        //                     <div className="single_footer">
        //                         <h3 className="footer-col-title">{t("legal")}</h3>
        //                         <ul className="footer-links">
        //                             <li><LangLink to="/privacy-policy">{t("privacy_policy")}</LangLink></li>
        //                             <li><LangLink to="/terms">{t("terms_of_use")}</LangLink></li>
        //                             <li><LangLink to="/gdpr">{t("gdpr")}</LangLink></li>
        //                             <li><LangLink to="/delete-account">{t("delete_account")}</LangLink></li>
        //                         </ul>
        //                     </div>
        //                 </div>

        //             </div>
        //         </div>
        //     </div>

        //     {/* Copyright bar */}
        //     <div className="copyright">
        //         <div className="container">
        //             <div className="row align-items-center gy-3">
        //                 <div className="col-12 col-md-6">
        //                     <p style={{ margin: 0, fontSize: '13px' }}>
        //                     </p>
        //                 </div>
        //                 <div className="col-12 col-md-6">
        //                     <div className="footer_social" style={{ textAlign: 'right' }}>
        //                         <ul className="footer-social-list">
        //                             <li><a href="https://www.facebook.com/profile.php?id=61553911298479" target="_blank" rel="noopener noreferrer" aria-label="AdCast on Facebook"><i className="fab fa-facebook-f"></i></a></li>
        //                             <li><a href="https://x.com/AdCastAppX" target="_blank" rel="noopener noreferrer" aria-label="AdCast on X"><i className="fab">𝕏</i></a></li>
        //                             <li><a href="https://discord.gg/zRAZa2nj7b" target="_blank" rel="noopener noreferrer" aria-label="AdCast on Discord"><i className="fab fa-discord"></i></a></li>
        //                             <li><a href="https://www.instagram.com/adcast.app" target="_blank" rel="noopener noreferrer" aria-label="AdCast on Instagram"><i className="fab fa-instagram"></i></a></li>
        //                             <li><a href="https://www.linkedin.com/company/adcast-digital-signage/" target="_blank" rel="noopener noreferrer" aria-label="AdCast on LinkedIn"><i className="fab fa-linkedin-in"></i></a></li>
        //                             <li><a href="https://youtube.com/@adcastdigitalsignage?si=8nQuO9E1XqPng5H9" target="_blank" rel="noopener noreferrer" aria-label="AdCast on YouTube"><i className="fab fa-youtube"></i></a></li>
        //                         </ul>
        //                     </div>
        //                 </div>
        //             </div>
        //         </div>
        //     </div>

        //     <CookieConsentBanner />
        // </footer>
    );
}

export default CallToAction;
